import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme, IconButton, Icon, Tooltip, IOption } from '../../../../base';
import {
    IClientSourceOfHireChartData,
    SourceOfHireBarMode,
    SourceOfHireDistributionMetric,
    SOURCE_OF_HIRE_DISTRIBUTION_METRICS,
    getSourceOfHireSourceTitles,
} from '../../../../common';
import SelectDropdown from '../../../SelectDropdown';
import SourceOfHireSourcesBarCard from './SourceOfHireSourcesBarCard';
import SourceOfHireStagesBarCard from './SourceOfHireStagesBarCard';
import {
    StyledBarViewWrapper,
    StyledToolbarRow,
    StyledDropdownSlot,
    StyledToolbarSpacer,
} from './BarView.styles';

interface IBarViewProps {
    chartData: IClientSourceOfHireChartData;
}

const BarView = ({ chartData }: IBarViewProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const sourceTitles = useMemo(
        () => getSourceOfHireSourceTitles(chartData),
        [chartData]
    );
    const sourceOptions: IOption[] = sourceTitles.map((title) => ({
        value: title,
        label: title,
    }));

    const [mode, setMode] = useState<SourceOfHireBarMode>(
        SourceOfHireBarMode.BY_SOURCE
    );
    const [metric, setMetric] =
        useState<SourceOfHireDistributionMetric>('hired');
    const [selectedSourceTitles, setSelectedSourceTitles] = useState<
        string[]
    >([]);
    const [selectedStageSource, setSelectedStageSource] = useState<
        string | null
    >(
        chartData.topStatistics.topSource.sourceTitle ??
            sourceTitles[0] ??
            null
    );

    const isBySource = mode === SourceOfHireBarMode.BY_SOURCE;

    const handleToggleMode = () => {
        setMode(
            isBySource
                ? SourceOfHireBarMode.BY_STAGE
                : SourceOfHireBarMode.BY_SOURCE
        );
    };

    const selectedSourceOptions = sourceOptions.filter((option) =>
        selectedSourceTitles.includes(option.value)
    );
    const selectedStageSourceOption =
        sourceOptions.find(
            (option) => option.value === selectedStageSource
        ) ?? null;

    const metricOptions: IOption[] = SOURCE_OF_HIRE_DISTRIBUTION_METRICS.map(
        (option) => ({ value: option.metric, label: t(option.shortLabelKey) })
    );
    const selectedMetricOption =
        metricOptions.find((option) => option.value === metric) ?? null;

    const handleMetricChange = (option: IOption | null) => {
        if (option) {
            setMetric(option.value as SourceOfHireDistributionMetric);
        }
    };

    const handleStageSourceChange = (option: IOption | null) => {
        if (option) {
            setSelectedStageSource(option.value);
        }
    };

    return (
        <StyledBarViewWrapper>
            <StyledToolbarRow container>
                <StyledDropdownSlot>
                    {isBySource ? (
                        <SelectDropdown
                            options={sourceOptions}
                            selected={null}
                            onChange={() => {}}
                            multiSelect
                            selectedMulti={selectedSourceOptions}
                            onMultiChange={(options) =>
                                setSelectedSourceTitles(
                                    options.map((option) => option.value)
                                )
                            }
                            placeholder={t(
                                'reportsPage.sourceOfHireReport.allSources'
                            )}
                        />
                    ) : (
                        <SelectDropdown
                            options={sourceOptions}
                            selected={selectedStageSourceOption}
                            onChange={handleStageSourceChange}
                            placeholder={t(
                                'reportsPage.sourceOfHireReport.allSources'
                            )}
                        />
                    )}
                </StyledDropdownSlot>
                {isBySource && (
                    <StyledDropdownSlot>
                        <SelectDropdown
                            options={metricOptions}
                            selected={selectedMetricOption}
                            onChange={handleMetricChange}
                            placeholder={t(
                                'reportsPage.sourceOfHireReport.hired'
                            )}
                        />
                    </StyledDropdownSlot>
                )}
                <StyledToolbarSpacer />
                <Tooltip
                    title={t('reportsPage.sourceOfHireReport.toggleBarView')}
                >
                    <IconButton size="small" onClick={handleToggleMode}>
                        <Icon
                            name="SyncOutlineIcon"
                            size={20}
                            style={{ color: theme.palette.secondary.main }}
                        />
                    </IconButton>
                </Tooltip>
            </StyledToolbarRow>
            {isBySource ? (
                <SourceOfHireSourcesBarCard
                    chartData={chartData}
                    metric={metric}
                    selectedSourceTitles={selectedSourceTitles}
                />
            ) : (
                <SourceOfHireStagesBarCard
                    chartData={chartData}
                    sourceTitle={selectedStageSource}
                />
            )}
        </StyledBarViewWrapper>
    );
};

export default BarView;
